import { EnvironmentInjector, inject, Injectable, runInInjectionContext } from '@angular/core';
import { NamedId } from '@type/name-id.type';
import { catchError, firstValueFrom, map, throwError } from 'rxjs';
import { DictionaryService } from 'src/app/core/services/dictionary/dictionary.service';
import { FilterHelperService } from '../filter-helper/filter-helper.service';

type Dict = {
  dict: NamedId[];
  total: number;
  empty?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class FilterDictionariesService {
  private ei = inject(EnvironmentInjector);

  #dictionaryForFilterMap = new Map<string, Dict>();

  #dictionaryMap = new Map<string, Dict>();

  #selectDictionaryMap = new Map<string, Dict>();

  setSelectDictionary(opts: Record<string, NamedId[]>) {
    this.#selectDictionaryMap.clear();
    Object.entries(opts).forEach(
      ([model, dict]) => dict && this.#selectDictionaryMap.set(model, { total: dict.length, dict }),
    );
  }

  extractDictionary(obj: Record<string, string>) {
    return Object.entries(obj).reduce((a, c) => {
      a.push({ id: c[0], name: c[1] });
      return a;
    }, new Array());
  }

  async getSelectDictionary(model: string): Promise<Dict> {
    return Promise.resolve(this.#selectDictionaryMap.get(model) || { total: 0, dict: [] });
  }

  async getFullDictionary(model: string): Promise<Dict> {
    return new Promise((resolve, reject) => {
      runInInjectionContext(this.ei, async () => {
        if (this.#dictionaryMap.has(model)) {
          resolve(this.#dictionaryMap.get(model)!);
        } else {
          const dictionary = inject(DictionaryService);
          const request = dictionary.createRequest({ model_name: model, q: {}, company: null });

          const result = await firstValueFrom(
            dictionary.getDictionary(request).pipe(
              map((res) => ({ dict: res['data'], total: res.meta.total_count })),
              catchError((err) => {
                reject({ dict: [], total: 0 });
                return throwError(() => err);
              }),
            ),
          );

          this.#dictionaryMap.set(model, result);
          resolve(result);
        }
      });
    });
  }

  async getDictionary(model: string, query = ''): Promise<Dict> {
    return new Promise((resolve, reject) => {
      runInInjectionContext(this.ei, async () => {
        if (this.#dictionaryForFilterMap.has(model)) {
          resolve(this.#dictionaryForFilterMap.get(model)!);
          return;
        }

        const dictionary = inject(DictionaryService);
        const { cacheDictionary } = inject(FilterHelperService).options();

        const result = await firstValueFrom(
          dictionary
            .getDictionary(
              dictionary.createRequest({
                q: { name_cont: query },
                model_name: model,
                company: null,
              }),
            )
            .pipe(
              map((res) => ({ dict: res['data'], total: res.meta.total_count })),
              catchError((err) => {
                reject({ dict: [], total: 0 });
                return throwError(() => err);
              }),
            ),
        );

        cacheDictionary && this.#dictionaryForFilterMap.set(model, result);

        resolve(result);
      });
    });
  }
}
